/**
 * SocialPostEditor — the one way to edit and act on a `social_calendar` row.
 *
 * FEAT-092 / ADR-022. A social post is reached from two screens: the
 * cross-piece calendar (`SocialCalendarView`, wrapped in its drawer) and the
 * single-piece Amplifications document (`PieceAmplificationsDoc`). Before this
 * component there were two implementations, and they had already drifted —
 * `AmplificationsPanel` never got BUG-083's platform check, so it would still
 * offer scheduling on a `linkedin_article` row that can never be scheduled on
 * its own, and it treated a `failed` row as editable where the drawer did not.
 *
 * What is legal for a row is decided here, once: the state-machine gating on
 * the action buttons, `publishesWithItsArticle` for the rows the schedule does
 * not belong to, and `describeSocialActionError` for what a refusal means.
 * Neither caller re-derives any of it.
 *
 * Presentation is the caller's business. This renders fields and actions with
 * no chrome of its own — the calendar wraps it in a DrawerShell, the piece
 * screen renders it inline in the main window, where nothing may overlay the
 * document.
 */

import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { API_BASE_URL, useApiMutation, useApiQuery } from "@/lib/api";
import {
  imagePickPatch,
  imagePickerLocked,
  pickerOptions,
  type ImageOptionsResponse,
} from "@/lib/post-image-picker";
import {
  describeSocialActionError,
  publishesWithItsArticle,
} from "@/lib/social-action-errors";
import { isHandedOff } from "@/lib/social-status";
import { usePieceLookup } from "@/lib/piece-lookup";
import {
  MIN_SCHEDULE_LEAD_MINUTES,
  scheduledAtTooSoon,
  isoToLocalInput,
  defaultScheduledAtLocal,
} from "@/lib/schedule-validation";
import { planApprove } from "@/lib/social-approve";
import { splitForX, weightedLength, X_FREE_LIMIT } from "@shared/x-thread";
import { useRole } from "@/lib/useRole";
import { describeDispatchError } from "@/lib/social-failure";

/**
 * The fields this editor reads. Deliberately structural rather than a named
 * type either caller must import: `SocialCalendarView`'s `SocialCalendarRow`
 * and `types/piece.ts`'s `PieceAmplificationPost` both already satisfy it,
 * and TypeScript says so the moment either stops.
 */
export interface SocialPostEditorRow {
  id: string;
  piece_id: number | null;
  platform: string;
  body: string;
  image_file_id: string | null;
  status: string;
  scheduled_at: string | null;
  approved_at: string | null;
  /** Set once Buffer or Zernio holds the post (BUG-102). */
  dispatch_post_id: string | null;
  published_url: string | null;
  created_at: string;
  /** FEAT-107 / S-344 — the provider's reason, on a `failed` post. */
  dispatch_error?: string | null;
}

export interface SocialPostEditorProps {
  row: SocialPostEditorRow;
  /** The row changed in place — refetch whatever list is showing it. */
  onChanged: () => void;
  /** The row is gone — close whatever was showing it. */
  onRemoved: () => void;
  /**
   * FEAT-092 — "Mark as Posted" (confirm a post that went out by hand) has
   * only ever been offered on the piece screen. Off by default so the
   * calendar's drawer keeps exactly the actions it had.
   */
  showMarkPosted?: boolean;
}

interface PatchBody {
  body?: string;
  image_file_id?: string | null;
  scheduled_at?: string | null;
}

interface FileUploadResponse {
  id: string;
  content_type: string;
  size: number;
  url: string;
}

/**
 * Platform char limits — enforced client-side before submit.
 * Server has its own (S-076 caps body field at the route).
 */
const PLATFORM_CHAR_LIMITS: Record<string, number> = {
  linkedin: 3000,
  linkedin_article: 3000,
  x: 280,
  twitter: 280,
  instagram: 2200,
  threads: 500,
  substack: 100_000,
};

export function SocialPostEditor({
  row,
  onChanged,
  onRemoved,
  showMarkPosted = false,
}: SocialPostEditorProps) {
  const qc = useQueryClient();
  const piece = usePieceLookup(row.piece_id);
  const pieceLabel = piece?.title ?? (row.piece_id ? `piece #${row.piece_id}` : "Ad-hoc post");

  const isPublished = row.status === "published";
  const isCancelled = row.status === "cancelled";
  const isPosted = row.status === "posted";
  const isFailed = row.status === "failed";
  // BUG-102 / ADR-025 — with Buffer or Zernio now: an edit here would change
  // only CCM's copy. Cancel is the only action left.
  const handedOff = isHandedOff(row);
  // FEAT-108 / S-438 — a Reviewer sees calendar posts but cannot change them.
  const { canEdit } = useRole();
  const roleReadOnly = !canEdit;
  const readOnly = isPublished || isCancelled || isPosted || isFailed || handedOff || roleReadOnly;
  // FEAT-107 / S-344 — a failed post can be deleted too (it never reached
  // the provider, or the provider already gave up on it).
  const canDelete =
    canEdit &&
    !handedOff &&
    (row.status === "draft" || row.status === "approved" || row.status === "scheduled" || isFailed);
  const failureReason = isFailed ? describeDispatchError(row.dispatch_error) : null;
  const canCancelHandoff =
    canEdit &&
    handedOff && !!row.scheduled_at && new Date(row.scheduled_at).getTime() > Date.now();

  // Editable form state
  const [body, setBody] = useState(row.body);
  const [scheduledAt, setScheduledAt] = useState<string>(
    row.scheduled_at ? isoToLocalInput(row.scheduled_at) : ""
  );
  const [imageFileId, setImageFileId] = useState<string | null>(row.image_file_id);
  const [imageLocalPreview, setImageLocalPreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  // FEAT-102 / S-413 — what this post may use (BUG-101): its piece's images
  // and its own uploads. The server refuses anything else.
  const imageOptionsKey = ["social-post-image-options", row.id] as const;
  const imageOptionsQuery = useApiQuery<ImageOptionsResponse>(
    [...imageOptionsKey],
    `/social-calendar/${row.id}/image-options`
  );
  const imageChoices = pickerOptions(imageOptionsQuery.data, imageFileId, API_BASE_URL);

  // FEAT-090 — the schedule belongs to the piece, not to this row.
  //
  // A linkedin_article amplification goes out when the browser service
  // publishes its article, so `pieces.publication_date` is what decides the
  // time and this row's `scheduled_at` is a copy that nothing reads. Leaving it
  // editable made it a promise the system would not keep — the same shape as
  // the Schedule button BUG-083 removed, one field down.
  //
  // Shown from the piece rather than from the row so it cannot drift: the two
  // are identical today (verified across rows 2067–2071), but only one of them
  // is the value the publisher acts on.
  const scheduleOwnedByPiece = publishesWithItsArticle(row.platform);
  const displayedScheduledAt =
    scheduleOwnedByPiece && piece?.publication_date
      ? isoToLocalInput(piece.publication_date)
      : scheduledAt;

  const [recentlySaved, setRecentlySaved] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishedUrlInput, setPublishedUrlInput] = useState("");
  // FEAT-107 / S-344 — Retry's optional time; empty means "in a few minutes"
  // (or the post's own time while it is still ahead).
  const [retryAtInput, setRetryAtInput] = useState("");

  // Reset transient saved badge.
  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  useEffect(() => {
    return () => {
      if (imageLocalPreview) URL.revokeObjectURL(imageLocalPreview);
    };
  }, [imageLocalPreview]);

  const platformLimit = PLATFORM_CHAR_LIMITS[row.platform.toLowerCase()] ?? 10_000;

  // X is the one platform where going over the limit is not an error. Past
  // 280 the post publishes as a thread, so the composer says how many posts
  // that will be rather than blocking a perfectly publishable body.
  //
  // It also counts the way X counts: a URL is 23 characters however long it
  // really is, and the amplifier is instructed to include the article link.
  // Plain `body.length` reports a post with a 76-character link as 53
  // characters over when it is comfortably under.
  const isX = row.platform.toLowerCase() === "x";
  const usedChars = isX ? weightedLength(body) : body.length;
  const xThread = isX && body.trim() ? splitForX(body, X_FREE_LIMIT) : [];
  const xWillSplit = isX && xThread.length > 1;
  const bodyTooLong = isX ? false : body.length > platformLimit;
  const charPercent = usedChars / platformLimit;

  const scheduleTooSoon = scheduledAtTooSoon(scheduledAt);
  // Only relevant to Save (PATCH) — Schedule always uses this value,
  // touched or not, so scheduleTooSoon alone gates that button. Save
  // shouldn't block an unrelated edit (e.g. fixing a typo in the body)
  // just because a pre-existing scheduled_at happens to be stale; it's
  // only this edit's problem if the date is actually part of it.
  const scheduledAtChanged =
    (scheduledAt ? new Date(scheduledAt).toISOString() : null) !==
    (row.scheduled_at ? new Date(row.scheduled_at).toISOString() : null);

  const dirty =
    body !== row.body || scheduledAtChanged || imageFileId !== row.image_file_id;

  // S-070 — state-machine actions. The server enforces
  //   draft → approved → scheduled
  // plus the terminal "published" (set by Blotato dispatcher).
  //
  // Reschedule: there is NO dedicated reschedule endpoint. Editing
  // `scheduled_at` on a scheduled or approved post is done through
  // the PATCH endpoint (already wired as Save) — the "reschedule"
  // story is implicit in editing the datetime + clicking Save.
  //
  // Delete: DELETE /social-calendar/:id removes the row (no soft delete).
  // BUG-102 — refused once the post is handed off; POST /:id/cancel takes
  // it back from Buffer/Zernio first, returning it to Approved with no time.

  const patchMutation = useApiMutation<unknown, PatchBody>(
    `/social-calendar/${row.id}`,
    "PATCH",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        setActionError(null);
        invalidateAll();
        onChanged();
      },
      onError: (err) => {
        // Not err.message: for an ApiError that is `${status} ${body}`, so a
        // refused image pick showed raw JSON. The server's message instead.
        setActionError(describeSocialActionError(err, "Save failed."));
      },
    }
  );

  // BUG-123 — Approve may carry `post_now` (only after the person confirms it).
  const approveMutation = useApiMutation<unknown, { post_now?: boolean } | undefined>(
    `/social-calendar/${row.id}/approve`,
    "POST",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        setActionError(null);
        invalidateAll();
        onChanged();
      },
      // BUG-083 — show what the server actually said. A 409 here is usually a
      // rule, not a race, and "close and reopen the drawer" is advice that
      // leads nowhere for a rule.
      onError: (err) => setActionError(describeSocialActionError(err, "Approve failed.")),
    }
  );

  const scheduleMutation = useApiMutation<unknown, { scheduled_at?: string }>(
    `/social-calendar/${row.id}/schedule`,
    "POST",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        setActionError(null);
        invalidateAll();
        onChanged();
      },
      onError: (err) => setActionError(describeSocialActionError(err, "Schedule failed.")),
    }
  );

  // FEAT-062 (S-313) — no dispatcher exists for these; the founder posts by
  // hand outside the app and confirms it here. Allowed from any non-posted
  // status, unlike approve/schedule's strict linear state machine — a run that
  // failed to dispatch may still have gone out by hand.
  const markPostedMutation = useApiMutation<unknown, { published_url?: string }>(
    `/social-calendar/${row.id}/mark-posted`,
    "POST",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        setActionError(null);
        invalidateAll();
        onChanged();
      },
      onError: (err) =>
        setActionError(describeSocialActionError(err, "Mark as posted failed.")),
    }
  );

  // FEAT-107 / S-344 — a failed post goes back to Approved with a time.
  const retryMutation = useApiMutation<unknown, { scheduled_at?: string }>(
    `/social-calendar/${row.id}/retry`,
    "POST",
    {
      onSuccess: () => {
        setActionError(null);
        setRetryAtInput("");
        invalidateAll();
        onChanged();
      },
      onError: (err) => setActionError(describeSocialActionError(err, "Retry failed.")),
    }
  );

  const deleteMutation = useApiMutation<void, void>(
    `/social-calendar/${row.id}`,
    "DELETE",
    {
      onSuccess: () => {
        invalidateAll();
        onRemoved();
      },
      onError: (err) => {
        setActionError(err.message ?? "Cancel failed.");
      },
    }
  );

  const cancelHandoffMutation = useApiMutation<unknown, void>(
    `/social-calendar/${row.id}/cancel`,
    "POST",
    {
      onSuccess: () => {
        setActionError(null);
        invalidateAll();
        onChanged();
      },
      onError: (err) => setActionError(describeSocialActionError(err, "Cancel failed.")),
    }
  );

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ["social-calendar"] });
  }

  /**
   * FEAT-102 / S-413 — picking an image (a thumbnail, a fresh upload, or
   * "Remove image") saves it straight away, through the same PATCH as Save.
   * If the server refuses it, the drawer goes back to the image it had.
   */
  function pickImage(fileId: string | null) {
    if (readOnly) return;
    const previous = imageFileId;
    const patch = imagePickPatch(previous, fileId);
    if (!patch) return;
    setImageFileId(fileId);
    setActionError(null);
    patchMutation.mutate(patch, {
      onError: () => setImageFileId(previous),
    });
  }

  function buildPatch(): PatchBody {
    const out: PatchBody = {};
    if (body !== row.body) out.body = body;
    if (imageFileId !== row.image_file_id) {
      out.image_file_id = imageFileId;
    }
    const sched =
      scheduledAt && scheduledAt.length > 0
        ? new Date(scheduledAt).toISOString()
        : null;
    const wasIso = row.scheduled_at ? new Date(row.scheduled_at).toISOString() : null;
    if (sched !== wasIso) out.scheduled_at = sched;
    return out;
  }

  /** Why the drawer's edits can't be saved right now, or null when they can. */
  function saveBlockedReason(): string | null {
    if (bodyTooLong) {
      return `Body exceeds the ${platformLimit}-character ${row.platform} limit.`;
    }
    // Re-check at click time, not just render time — the whole point of
    // this guard is a value that was fine when typed can go stale while
    // the form just sits open. Only blocks the save when the schedule
    // time is actually part of this edit; an untouched, pre-existing
    // stale value doesn't get resent by buildPatch() and shouldn't block
    // an unrelated change (e.g. fixing a typo in the body).
    if (scheduledAtChanged && scheduledAtTooSoon(scheduledAt)) {
      return `Scheduled time must be at least ${MIN_SCHEDULE_LEAD_MINUTES} minutes from now.`;
    }
    return null;
  }

  function handleSave() {
    if (!dirty || patchMutation.isPending || readOnly) return;
    const blocked = saveBlockedReason();
    if (blocked) {
      setActionError(blocked);
      return;
    }
    setActionError(null);
    patchMutation.mutate(buildPatch());
  }

  // BUG-123 — Approve used to send only "approve": a time or text typed here
  // and not saved was dropped, and a draft with no time was posted on the
  // spot. Now unsaved edits are saved first, posting now is asked for, and a
  // Note without its link is flagged (the person may still approve it).
  async function handleApprove() {
    if (approveMutation.isPending || patchMutation.isPending || readOnly) return;
    setActionError(null);
    const plan = planApprove({ dirty, scheduledAt, platform: row.platform, body });
    if (
      plan.warnNoLink &&
      !window.confirm(
        "This Note has no link to its article yet. It is added automatically when the article publishes. Approve it without the link?"
      )
    ) {
      return;
    }
    if (
      plan.needsPostNow &&
      !window.confirm("This post has no send time, or its time has passed. Approving it posts it now. Post now?")
    ) {
      return;
    }
    if (plan.saveFirst) {
      const blocked = saveBlockedReason();
      if (blocked) {
        setActionError(blocked);
        return;
      }
      try {
        await patchMutation.mutateAsync(buildPatch());
      } catch {
        return; // the save's own onError has already shown why
      }
    }
    approveMutation.mutate(plan.needsPostNow ? { post_now: true } : undefined);
  }

  function handleSchedule() {
    if (scheduleMutation.isPending || readOnly) return;
    // The server only accepts approved → scheduled. We hide the
    // Schedule button on non-approved rows, but defend just in case.
    if (row.status !== "approved") {
      setActionError(
        `Only approved posts can be scheduled — current status is "${row.status}".`
      );
      return;
    }
    // Re-check at click time — scheduledAt is initialized from the row's
    // existing value when one exists, so this also catches a time that was
    // fine when the row was approved but has since gone stale (the actual
    // founder-hit case: the approve round-trip alone ate the whole buffer).
    if (scheduledAtTooSoon(scheduledAt)) {
      setActionError(
        `Scheduled time must be at least ${MIN_SCHEDULE_LEAD_MINUTES} minutes from now. Pick a new time and save before scheduling.`
      );
      return;
    }
    // If the user edited the datetime, pass it explicitly. Otherwise
    // the route will use the row's existing scheduled_at (and 422 if
    // null), which surfaces back through actionError.
    const sched =
      scheduledAt && scheduledAt.length > 0
        ? new Date(scheduledAt).toISOString()
        : undefined;
    scheduleMutation.mutate(sched ? { scheduled_at: sched } : {});
  }

  function handleMarkPosted() {
    if (markPostedMutation.isPending) return;
    setActionError(null);
    const url = publishedUrlInput.trim();
    markPostedMutation.mutate(url ? { published_url: url } : {});
  }

  function handleRetry() {
    if (retryMutation.isPending) return;
    setActionError(null);
    const at = retryAtInput ? new Date(retryAtInput).toISOString() : undefined;
    retryMutation.mutate(at ? { scheduled_at: at } : {});
  }

  function handleCancelHandoff() {
    if (cancelHandoffMutation.isPending || !canCancelHandoff) return;
    if (
      !window.confirm(
        "Cancel this post? It will be removed from Buffer or Zernio and return to Approved with no time, so you can edit it and schedule it again."
      )
    ) {
      return;
    }
    setActionError(null);
    cancelHandoffMutation.mutate();
  }

  function handleCancel() {
    if (deleteMutation.isPending || !canDelete) return;
    if (
      !window.confirm(
        "Delete this social post? The row will be removed from the calendar. There is no undo."
      )
    ) {
      return;
    }
    setActionError(null);
    deleteMutation.mutate();
  }

  return (
    <>
      {readOnly && (
        // Founder feedback (2026-09-19) — this is status information, not
        // an error to fix, even for isFailed (the failure already
        // happened and is described elsewhere; this line only explains
        // why the fields below are disabled). Styled as an info banner,
        // not settings-form-error's red.
        <div className="team-banner" style={{ marginTop: 12 }}>
          {isPublished && "This post has been published; edits are read-only."}
          {isPosted && "This post has been marked posted; edits are read-only."}
          {isCancelled && "This post has been cancelled; edits are disabled."}
          {isFailed &&
            (canEdit
              ? "This post failed to post. Retry it, confirm you posted it yourself, or delete it."
              : "This post failed to post.")}
          {handedOff &&
            !roleReadOnly &&
            "Handed off to the publishing service. Cancel it to make changes."}
          {roleReadOnly &&
            !isPublished && !isPosted && !isCancelled && !isFailed &&
            "Reviewers can see this post; an Admin or Editor changes it."}
        </div>
      )}

      <label className="settings-field">
        <span className="settings-label">
          Scheduled at{" "}
          <span className="settings-label-hint">
            {scheduleOwnedByPiece
              ? `Local time. Set by ${pieceLabel} — this post goes out with its article, so it cannot be scheduled separately.`
              : `Local time. Must be at least ${MIN_SCHEDULE_LEAD_MINUTES} minutes from now when scheduling.`}
          </span>
        </span>
        <input
          type="datetime-local"
          className="settings-input"
          value={displayedScheduledAt}
          disabled={readOnly || scheduleOwnedByPiece}
          onChange={(e) => {
            // Founder feedback (2026-09-19) — self-correct instead of
            // flagging red: picking today's date often leaves the time
            // portion at midnight or otherwise inside the lead-time
            // buffer (e.g. a native date-only pick with no explicit
            // time). Snap straight to a safely-future value rather than
            // accepting a choice we already know will fail, so a founder
            // isn't left staring at an error for a value they never
            // deliberately chose.
            const next = e.target.value;
            setScheduledAt(scheduledAtTooSoon(next) ? defaultScheduledAtLocal() : next);
          }}
        />
        {/* Not shown when read-only (no edit is possible, so a lead-time
            warning about a value that can never be resubmitted is just
            noise) or when the piece owns the schedule (same reasoning —
            the lead-time rule guards a scheduling request this row can
            never make). The onChange handler above already keeps a
            freshly-picked value out of this window; this only fires if
            enough time passes with the form open that a previously
            fine value goes stale before Save/Schedule is clicked. */}
        {scheduleTooSoon && !scheduleOwnedByPiece && !readOnly && (
          <span className="settings-field-error">
            Must be at least {MIN_SCHEDULE_LEAD_MINUTES} minutes from now — by
            the time a save or schedule request reaches the server, anything
            closer can already be in the past.
          </span>
        )}
      </label>

      <label className="settings-field">
        <span className="settings-label">
          Body{" "}
          <span
            className={`settings-label-hint cal-drawer-charcount ${
              bodyTooLong ? "is-over" : charPercent >= 0.9 ? "is-warn" : ""
            }`}
          >
            {usedChars.toLocaleString()} / {platformLimit.toLocaleString()} chars
          </span>
        </span>
        <textarea
          className="settings-input cal-drawer-textarea"
          value={body}
          disabled={readOnly}
          rows={10}
          onChange={(e) => setBody(e.target.value)}
        />
        {bodyTooLong && (
          <span className="settings-field-error">
            Body exceeds the {platformLimit}-character {row.platform} limit.
          </span>
        )}
        {/* BUG-123 / ADR-027 — a Note waits for its article's link. */}
        {!readOnly && planApprove({ dirty, scheduledAt, platform: row.platform, body }).warnNoLink && (
          <span className="settings-field-hint">
            No link to the article yet. It is added automatically when the article publishes.
          </span>
        )}
        {xWillSplit && (
          <span className="settings-field-hint">
            Over {X_FREE_LIMIT} characters — this will publish as a thread of{" "}
            {xThread.length} posts. An X account with a paid subscription posts
            it whole instead; we ask X which applies at publish time.
          </span>
        )}
      </label>

      {/* A div, not a label: a label activates its first control on any click,
          which here would be the first thumbnail. */}
      <div className="settings-field">
        <span className="settings-label">
          Image{" "}
          <span className="settings-label-hint">
            Optional. One of this piece's images, or upload one for this post
            (PNG, JPG, WebP, or SVG up to 25 MB).
          </span>
        </span>
        {imageChoices.length > 0 && (
          <div className="post-image-picker" role="radiogroup" aria-label="Post image">
            {imageChoices.map((o) => (
              <button
                key={o.fileId}
                type="button"
                role="radio"
                aria-checked={o.selected}
                className={`post-image-option${o.selected ? " is-selected" : ""}`}
                disabled={imagePickerLocked({
                  readOnly,
                  saving: patchMutation.isPending,
                  uploading: imageUploading,
                })}
                onClick={() => {
                  // The uploader's preview follows the pick, not the last upload.
                  setImageLocalPreview(null);
                  pickImage(o.fileId);
                }}
                title={o.label}
              >
                <img src={o.thumbUrl} alt={o.label} loading="lazy" />
                <span className="post-image-option-label">{o.label}</span>
              </button>
            ))}
          </div>
        )}
        {imageOptionsQuery.isError && (
          <span className="settings-field-hint">Could not load this post's images.</span>
        )}
        <ImageUploader
          socialPostId={row.id}
          currentFileId={imageFileId}
          localPreview={imageLocalPreview}
          uploading={imageUploading}
          error={imageUploadError}
          // A pick's PATCH in flight locks the uploader and Remove too, for
          // the same reason the thumbnails are locked (imagePickerLocked).
          disabled={readOnly || patchMutation.isPending}
          onUploadStart={() => {
            setImageUploading(true);
            setImageUploadError(null);
          }}
          onUploaded={(fileId, localPreview) => {
            setImageUploading(false);
            setImageUploadError(null);
            if (imageLocalPreview) URL.revokeObjectURL(imageLocalPreview);
            setImageLocalPreview(localPreview);
            // The upload is now one of this post's options; pick it.
            void qc.invalidateQueries({ queryKey: [...imageOptionsKey] });
            pickImage(fileId);
          }}
          onUploadError={(msg) => {
            setImageUploading(false);
            setImageUploadError(msg);
          }}
          onClear={() => {
            if (imageLocalPreview) URL.revokeObjectURL(imageLocalPreview);
            setImageLocalPreview(null);
            pickImage(null);
          }}
        />
      </div>

      {failureReason && (
        <div className="settings-form-error" style={{ marginTop: 8 }}>
          Couldn&rsquo;t publish: {failureReason}
        </div>
      )}

      {isFailed && canEdit && (
        <label className="settings-field">
          <span className="settings-label">
            Retry{" "}
            <span className="settings-label-hint">
              Leave the time empty to send in a few minutes (or at its own time, if that is still ahead).
            </span>
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="datetime-local"
              className="settings-input"
              value={retryAtInput}
              onChange={(e) => setRetryAtInput(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn-primary"
              disabled={retryMutation.isPending}
              onClick={handleRetry}
            >
              {retryMutation.isPending ? "Retrying…" : "Retry"}
            </button>
          </div>
        </label>
      )}

      {/* FEAT-092 — offered wherever the caller asks for it, on any row that
          has not already been marked posted. Deliberately NOT gated by
          `readOnly`: a `failed` row is exactly the one someone posts by hand
          and then confirms here, which is the behaviour AmplificationsPanel
          had before this component replaced it. */}
      {(showMarkPosted || isFailed) && !isPosted && canEdit && (
        <label className="settings-field">
          <span className="settings-label">
            {isFailed ? "I posted it myself" : "Posted by hand?"}{" "}
            <span className="settings-label-hint">
              {isFailed
                ? "Paste the post's link. For a Buffer post you resent inside Buffer you can leave it empty: CCM will ask Buffer for it."
                : "Confirm this went out outside the app. The URL is optional."}
            </span>
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="url"
              className="settings-input"
              placeholder="Posted URL (optional)"
              value={publishedUrlInput}
              onChange={(e) => setPublishedUrlInput(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn-ghost"
              disabled={markPostedMutation.isPending}
              onClick={handleMarkPosted}
            >
              {markPostedMutation.isPending ? "Marking…" : "Mark as Posted"}
            </button>
          </div>
        </label>
      )}

      {actionError && (
        <div className="settings-form-error" style={{ marginTop: 8 }}>
          {actionError}
        </div>
      )}

      <div className="cal-drawer-actions">
        <div className="cal-drawer-actions-left">
          {!readOnly && row.status === "draft" && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => void handleApprove()}
              disabled={approveMutation.isPending || patchMutation.isPending}
              title="Move from draft to approved. Schedule next."
            >
              {approveMutation.isPending ? "Approving…" : "Approve"}
            </button>
          )}
          {/* BUG-083 — a LinkedIn article post cannot be scheduled: it goes
              out with its article, filling the Create post modal when the
              browser service publishes (FEAT-086 / S-386). Offering the
              button and answering 409 is how this looked like a stale
              drawer for an hour. Say so instead.

              Present tense on purpose. The row is `approved` here, i.e. it
              has NOT gone out yet — "published" would read as a claim that
              it already had, which is the same kind of untruth the button
              was telling. */}
          {!readOnly &&
            row.status === "approved" &&
            (publishesWithItsArticle(row.platform) ? (
              <p className="muted" style={{ margin: 0 }}>
                {row.piece_id
                  ? `This post publishes with piece #${row.piece_id}.`
                  : "This post publishes with its article."}
              </p>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={handleSchedule}
                disabled={scheduleMutation.isPending || scheduleTooSoon}
                title={
                  scheduledAt
                    ? "Schedules with the current Scheduled-at value"
                    : "Schedules with the existing scheduled_at on the row"
                }
              >
                {scheduleMutation.isPending ? "Scheduling…" : "Schedule"}
              </button>
            ))}
          {canCancelHandoff && (
            <button
              type="button"
              className="btn-ghost btn-danger"
              onClick={handleCancelHandoff}
              disabled={cancelHandoffMutation.isPending}
              title="Remove this post from Buffer or Zernio and return it to Approved with no time."
            >
              {cancelHandoffMutation.isPending ? "Cancelling…" : "Cancel post"}
            </button>
          )}
          {canDelete && (
              <button
                type="button"
                className="btn-ghost btn-danger"
                onClick={handleCancel}
                disabled={deleteMutation.isPending}
                title="Remove this row from the calendar. No undo."
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            )}
        </div>
        <div className="cal-drawer-actions-right">
          {recentlySaved && !dirty && (
            <span className="editor-saved-badge" role="status">
              Saved
            </span>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={
              !dirty ||
              patchMutation.isPending ||
              readOnly ||
              bodyTooLong ||
              (scheduledAtChanged && scheduleTooSoon)
            }
          >
            {patchMutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------- Image uploader (mirrors BrandIdentityEditor's LogoUploader) ----------

interface ImageUploaderProps {
  /** FEAT-102 / S-413 — the upload belongs to this post (and only this post). */
  socialPostId: string;
  currentFileId: string | null;
  localPreview: string | null;
  uploading: boolean;
  error: string | null;
  disabled?: boolean;
  onUploaded: (fileId: string, localPreview: string) => void;
  onUploadStart: () => void;
  onUploadError: (msg: string) => void;
  onClear: () => void;
}

function ImageUploader({
  socialPostId,
  currentFileId,
  localPreview,
  uploading,
  error,
  disabled,
  onUploaded,
  onUploadStart,
  onUploadError,
  onClear,
}: ImageUploaderProps) {
  const { getToken } = useAuth();

  const previewSrc =
    localPreview ??
    (currentFileId ? `${API_BASE_URL}/assets/${currentFileId}?width=240` : null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file) return;
      onUploadStart();
      try {
        const token = await getToken({ template: "ccm-default" });
        const fd = new FormData();
        fd.append("file", file);
        fd.append("social_post_id", socialPostId);
        const res = await fetch(`${API_BASE_URL}/files`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: fd,
        });
        if (!res.ok) {
          const text = await res.text();
          onUploadError(`Upload failed (${res.status}): ${text || "unknown"}`);
          return;
        }
        const data = (await res.json()) as FileUploadResponse;
        const objectUrl = URL.createObjectURL(file);
        onUploaded(data.id, objectUrl);
      } catch (err) {
        onUploadError(err instanceof Error ? err.message : "Upload failed");
      }
    },
    [getToken, socialPostId, onUploadStart, onUploaded, onUploadError]
  );

  return (
    <div className="brand-logo-row">
      <div className="brand-logo-preview">
        {previewSrc ? (
          <img src={previewSrc} alt="Post image preview" />
        ) : (
          <div className="brand-logo-preview-empty">No image</div>
        )}
      </div>
      <div className="brand-logo-controls">
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          disabled={uploading || disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />
        {uploading && (
          <div className="brand-logo-status is-uploading">Uploading to R2…</div>
        )}
        {error && <div className="brand-logo-status is-error">{error}</div>}
        {currentFileId && !uploading && !disabled && (
          <button
            type="button"
            className="btn-ghost"
            onClick={onClear}
            style={{ alignSelf: "flex-start" }}
          >
            Remove image
          </button>
        )}
      </div>
    </div>
  );
}
